import { WatchQueryFetchPolicy } from '@apollo/client';
import { createError } from '@common/neo.tax';
import { useEffect, useMemo } from 'react';

import {
  useSticky,
  GET_RD_PROJECTS,
  GetRdProjectsQuery,
  GetRdProjectsQueryVariables,
  RdProjectFilter,
  RdProjectSort,
  useCursorPaginatedQuery,
  Conditions,
  advancedFilter,
  FilterField,
  keywordFilter,
} from '@neo.tax/shared';

import { useRdContext } from '../context';

const GET_RD_PROJECTS_KEY = 'GET_RD_PROJECTS_KEY';

export type RdProject = NonNullable<GetRdProjectsQuery['getRdProjects']['edges']>[number]['node'];

interface UseRdProjectsProps {
  filterConditions?: Conditions<FilterField<RdProjectFilter>>;
  numElementsInitialFetch?: number;
  searchKeyword?: string | null;
  skip?: boolean;
  sorting?: Array<RdProjectSort> | RdProjectSort;
  fetchAll?: boolean;
  fetchPolicy?: WatchQueryFetchPolicy;
}

export const useRdProjects = ({
  fetchAll = false,
  fetchPolicy,
  filterConditions,
  numElementsInitialFetch,
  searchKeyword,
  skip = false,
  sorting,
}: UseRdProjectsProps = {}): {
  loading: boolean;
  rdProjects: RdProject[] | null;
  hasNextPage: boolean;
  onLoadMoreItems: () => Promise<void>;
  isLoadingMore: boolean;
} => {
  const { fiscalYearId } = useRdContext();

  const {
    data: requestData,
    error,
    hasNextPage,
    isLoadingMore,
    loading,
    onLoadMoreItems,
  } = useCursorPaginatedQuery<GetRdProjectsQuery, GetRdProjectsQueryVariables>(
    GET_RD_PROJECTS,
    {
      variables: {
        fiscalYearId,
        filter: {
          and: [
            keywordFilter(['name', 'projectOwnerName'], searchKeyword),
            advancedFilter<RdProjectFilter>(filterConditions),
          ],
        },
        sorting: sorting ?? [],
      },
      context: {
        debounceKey: GET_RD_PROJECTS_KEY,
      },
      fetchPolicy,
      skip,
    },
    {
      getConnection: (data) => data.getRdProjects,
      numElementsPerFetch: 40,
      numElementsInitialFetch,
    },
  );

  // Auto-pager: when fetchAll=true, keep fetching until hasNextPage is false
  useEffect(() => {
    if (!fetchAll || skip) return;

    let cancelled = false;
    let inFlight = false;

    const loadAll = async (): Promise<void> => {
      if (inFlight || cancelled) return;
      inFlight = true;
      try {
        // loop until server says we're done
        while (!cancelled && hasNextPage) {
          await onLoadMoreItems();
        }
      } finally {
        inFlight = false;
      }
    };

    // Only start auto-pagination after we have initial data
    if (!loading && requestData) {
      void loadAll();
    }

    return (): void => {
      cancelled = true;
    };
  }, [fetchAll, skip, hasNextPage, loading, onLoadMoreItems, requestData]);

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const rdProjectNodes = stickyData?.getRdProjects?.edges.map((edge) => edge.node) ?? null;

  const rdProjects = useMemo(() => {
    if (!rdProjectNodes) {
      return null;
    }

    return rdProjectNodes;
  }, [rdProjectNodes]);

  if (loading && !rdProjects) {
    return {
      loading: true,
      rdProjects: null,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
      isLoadingMore: false,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData) {
    // During fetchAll auto-pagination, we might temporarily have no sticky data
    // Only throw if we're not in fetchAll mode AND we're not currently loading
    if (!fetchAll && !loading) {
      throw createError('MissingRdProjects', 'No data returned from getRdProjects query');
    }
    // Return loading state during auto-fetch or during normal fetchAll operation
    return {
      loading: true,
      rdProjects: null,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
      isLoadingMore: false,
    };
  }

  return {
    loading,
    rdProjects,
    hasNextPage,
    onLoadMoreItems,
    isLoadingMore,
  };
};
